import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, RefreshCw, Clock, Edit, Sparkles } from 'lucide-react';

export default function ResumeRefreshPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.resumeRefresh} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Quick Updates</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Refresh & Editing Service
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Not ready for a full rewrite? Our resume refresh service fixes formatting, improves clarity, and updates your experience without starting from scratch.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* What's Included */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            What's Included in a Resume Refresh
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Edit, title: 'Formatting Fixes', desc: 'Clean up inconsistent formatting, fix alignment issues, and improve visual presentation.' },
              { icon: Sparkles, title: 'Clarity Improvements', desc: 'Tighten language, remove jargon, and make your experience easier to understand.' },
              { icon: RefreshCw, title: 'Recent Experience Updates', desc: 'Add your most recent role or update existing positions with new achievements.' },
              { icon: Clock, title: 'Fast Turnaround', desc: 'Resume refreshes are typically completed within 24-48 hours after payment.' },
              { icon: Check, title: 'ATS Check', desc: 'Basic ATS compatibility review to ensure your resume can be parsed correctly.' },
              { icon: Check, title: 'PDF & DOCX Delivery', desc: 'Receive both formats for immediate use and future editing.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Refresh vs Rewrite */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Resume Refresh vs. Full Rewrite
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6 rounded-2xl border-2 border-slate-200">
                <h3 className="font-bold text-slate-900 mb-4 text-lg">Resume Refresh</h3>
                <p className="text-slate-600 mb-4">Best when your resume structure is solid but needs polish and updates.</p>
                <ul className="space-y-2">
                  {['Formatting cleanup', 'Grammar and clarity fixes', 'Add recent experience', 'Basic ATS check', 'Starting at $59'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-teal-600" /> {item}
                    </li>
                  ))}
                </ul>
              </Card>
              
              <Card className="p-6 rounded-2xl border-2 border-teal-500 bg-teal-50/30">
                <h3 className="font-bold text-slate-900 mb-4 text-lg">Full Rewrite</h3>
                <p className="text-slate-600 mb-4">Best when you need a complete transformation for a new direction.</p>
                <ul className="space-y-2">
                  {['Complete content rewrite', 'Achievement-focused bullets', 'Keyword optimization', 'Professional summary', 'Starting at $99'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-teal-600" /> {item}
                    </li>
                  ))}
                </ul>
                <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 text-sm underline mt-4 block">
                  Learn more about full resume rewrites →
                </Link>
              </Card>
            </div>
            
            <p className="text-center text-slate-600 mt-8">
              Not sure which is right for you? <Link to="/start" className="text-teal-700 underline">Submit your intake</Link> and we'll recommend the best option after reviewing your resume.
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Full Resume Rewrite</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/linkedin-optimization" className="text-teal-700 hover:text-teal-800 underline">LinkedIn Optimization</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/resume-review" className="text-teal-700 hover:text-teal-800 underline">Resume Review</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View All Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Need a quick resume refresh?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your resume to confirm if a refresh is right for you.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
